import { type MutableRefObject, useCallback, useLayoutEffect, useRef } from 'react';

export function useScrollSync(...refs: MutableRefObject<HTMLElement>[]) {
  const refsRef = useRef(refs);
  refsRef.current = refs;

  const leader = useRef<HTMLElement | null>(null);
  const rafId = useRef<number | null>(null);
  const idleId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressed = useRef(false);
  const lastSynced = useRef<number | null>(null);

  const propagate = useCallback((left: number, except: HTMLElement | null) => {
    lastSynced.current = left;
    for (const ref of refsRef.current) {
      const el = ref.current;
      if (el && el !== except && el.scrollLeft !== left) {
        el.scrollLeft = left;
      }
    }
  }, []);

  const stop = useCallback(() => {
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
    leader.current = null;
  }, []);

  const tick = useCallback(() => {
    const lead = leader.current;
    if (lead && lead.scrollLeft !== lastSynced.current) {
      propagate(lead.scrollLeft, lead);
    }
    rafId.current = requestAnimationFrame(tick);
  }, [propagate]);

  const handleEventScroll = useCallback(
    (evt: Event) => {
      if (suppressed.current) return;
      const target = evt.target as HTMLElement;
      if (target !== leader.current && target.scrollLeft === lastSynced.current) return;

      leader.current = target;
      propagate(target.scrollLeft, target);

      if (rafId.current === null) rafId.current = requestAnimationFrame(tick);
      if (idleId.current) clearTimeout(idleId.current);
      idleId.current = setTimeout(stop, 150);
    },
    [propagate, tick, stop],
  );

  const handleScroll = useCallback((scroll: number, smooth = false, origin: HTMLElement | null = null) => {
    suppressed.current = true;
    lastSynced.current = scroll;
    for (const ref of refsRef.current) {
      const el = ref.current;
      if (!el || el === origin) continue;
      if (smooth) el.scrollTo({ left: scroll, behavior: 'smooth' });
      else el.scrollLeft = scroll;
    }
    if (suppressId.current) clearTimeout(suppressId.current);
    suppressId.current = setTimeout(
      () => {
        suppressed.current = false;
      },
      smooth ? 800 : 150,
    );
  }, []);

  useLayoutEffect(() => {
    const list = refsRef.current;
    for (const ref of list) {
      ref.current?.addEventListener('scroll', handleEventScroll, { passive: true });
    }
    return () => {
      for (const ref of list) {
        ref.current?.removeEventListener('scroll', handleEventScroll);
      }
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
      if (idleId.current) clearTimeout(idleId.current);
      if (suppressId.current) clearTimeout(suppressId.current);
    };
  }, [handleEventScroll]);

  return handleScroll;
}
